import React, { useState, useEffect, lazy, Suspense, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  TrendingUp,
  Play, Star, Users,
  BarChart3, Database, Sparkles, Palette, Target
} from 'lucide-react';
import FeatureSection from '../components/FeatureSection';
import MarketingChannels from '../components/MarketingChannels';
import GlowyAIChat from '../components/GlowyAIChat';
import DeviceIntegration from '../components/DeviceIntegration';
import DataFlowAnimation from '../components/DataFlowAnimation';

// Performance optimization - Lazy load sections that are below the fold
const IntegrationSection = lazy(() => import('../components/IntegrationSection'));
const CTASection = lazy(() => import('../components/CTASection'));
const Footer = lazy(() => import('../components/Footer'));

// Loading fallback
const SectionLoader = () => (
  <div className="py-24 flex justify-center items-center">
    <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

export default function FrontPage() {
  const [scrolled, setScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState({
    features: false,
    integrations: false,
    cta: false,
    detailedFeatures: false,
    marketingChannels: false
  });
  
  // Ref tanımlamaları
  const featuresRef = useRef(null);
  const integrationsRef = useRef(null);
  const ctaRef = useRef(null);
  const detailedFeaturesRef = useRef(null);
  const marketingChannelsRef = useRef(null);

  // Daha verimli scroll kontrolü için Intersection Observer kullanımı
  useEffect(() => {
    // Navbar için scroll kontrolü - daha verimli throttling
    let lastScrollY = 0;
    let ticking = false;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrolled(currentScrollY > 20);
          lastScrollY = currentScrollY;
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Intersection Observer ile görünürlük kontrolü
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };
    
    const sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const id = entry.target.id;
          
          if (id === 'detailed-features') {
            setIsVisible(prev => ({ ...prev, detailedFeatures: true }));
          } else if (id === 'cta') {
            setIsVisible(prev => ({ ...prev, cta: true }));
          } else if (id === 'marketing-channels') {
            setIsVisible(prev => ({ ...prev, marketingChannels: true }));
          }
          
          // Görünür olduktan sonra gözlemlemeyi durdur (performans için)
          sectionObserver.unobserve(entry.target);
        }
      });
    }, observerOptions);
    
    // Gözlemlenecek elementleri ekle
    const sections = [
      document.getElementById('detailed-features'),
      document.getElementById('cta'),
      document.getElementById('marketing-channels')
    ];
    
    sections.forEach(section => {
      if (section) sectionObserver.observe(section);
    });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      sections.forEach(section => {
        if (section) sectionObserver.unobserve(section);
      });
    };
  }, []);

  const menuItems = [
    { name: 'Features', href: '#features' },
    { name: 'Solutions', href: '#solutions' },
    { name: 'Integrations', href: '#integrations' },
    { name: 'Pricing', href: '#pricing' },
    { name: 'Resources', href: '#resources' },
  ];

  // Memoize the hero image to prevent re-renders
  const heroImage = React.useMemo(() => (
    <img
      src="/Screenshot2025-03-1501.04.29.png"
      alt="Analytics Dashboard"
      className="w-full h-auto object-contain max-w-2xl mx-auto"
      loading="eager"
      width="800"
      height="800"
    />
  ), []);

  const features = [
    {
      icon: <Database className="w-10 h-10" />,
      title: 'Seamless Data Integration',
      description: 'Connect all your marketing platforms with one-click integrations. Import your data automatically and keep everything in sync.',
      image: '/3d-data-integration.png',
      color: 'from-purple-400 to-purple-500',
      reverse: true
    },
    {
      icon: <Sparkles className="w-10 h-10" />,
      title: 'AI-Powered Optimization',
      description: 'Let our advanced AI analyze your campaigns and suggest improvements. Maximize your ROI with intelligent recommendations.',
      image: '/3d-ai-optimization.png',
      color: 'from-cyan-400 to-blue-500'
    }
  ];

  const [selectedDashboard, setSelectedDashboard] = useState('campaign');

  // Dashboard görsellerini tanımlayalım
  const dashboardImages = {
    campaign: '/Screenshot2025-0401t21.27.23.png',
    creative: '/creativeperformance.png',
    audience: '/audienceinsights.png'
  };

  // Dashboard verilerini tanımlayalım
  const dashboards = [
    {
      id: 'campaign',
      title: 'Campaign Overview',
      description: 'Real-time campaign metrics, budget tracking, and performance analytics',
      icon: <BarChart3 className="w-6 h-6" />,
      stats: {
        label: 'Conversion Rate',
        value: '+47% MoM'
      }
    },
    {
      id: 'creative',
      title: 'Creative Performance',
      description: 'Visual analytics, engagement metrics, and creative insights',
      icon: <Star className="w-6 h-6" />,
      stats: {
        label: 'Engagement Rate',
        value: '+32% MoM'
      }
    },
    {
      id: 'audience',
      title: 'Audience Insights',
      description: 'Demographic data, behavior analysis, and segmentation metrics',
      icon: <Users className="w-6 h-6" />,
      stats: {
        label: 'Audience Growth',
        value: '+28% MoM'
      }
    }
  ];

  return (
    <div className="min-h-screen bg-white text-gray-900 overflow-hidden">
      {/* Navigation - Light Version */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-lg py-3' : 'bg-transparent py-5'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <img src="/Calisma-Yuzeyi-20@5x-4.png" alt="Glowytics" className="h-8 w-auto" />
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              {menuItems.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="text-gray-600 hover:text-gray-900 px-3 py-2 text-base font-medium transition-colors"
                >
                  {item.name}
                </a>
              ))}
            </div>

            <div className="flex items-center space-x-4">
              <Link
                to="/login"
                className="text-gray-600 hover:text-gray-900 px-3 py-2 text-base font-medium transition-colors"
              >
                Log in
              </Link>
              <Link
                to="/signup"
                className="bg-blue-500 text-white px-6 py-3 rounded-full text-base font-medium hover:bg-blue-600 transition-all duration-300 shadow-sm"
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Light Version */}
      <div className="relative pt-32 pb-20 sm:pt-40 sm:pb-24 overflow-hidden bg-gradient-to-b from-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              
              <h1 className="text-5xl font-bold tracking-tight text-gray-900 sm:text-6xl">
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 pb-2">
                  Marketing Intelligence
                </span>
                <span className="mt-4 block">
                  Platform
                </span>
              </h1>
              <p className="mt-8 text-lg text-gray-600 leading-relaxed">
                <span className="font-semibold">AI-powered marketing management.</span> Manage all your marketing platforms in one place. Get AI-powered insights, 
                optimize your campaigns, and make data-driven decisions with ease.
              </p>
              <div className="mt-8 flex items-center gap-4">
                <Link
                  to="/signup"
                  className="inline-flex items-center px-6 py-3.5 bg-blue-500 text-white font-medium rounded-full hover:bg-blue-600 transition-all duration-300 shadow-sm"
                >
                  Start Free Trial
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
                <Link
                  to="/demo"
                  className="inline-flex items-center px-6 py-3.5 bg-white text-gray-700 font-medium rounded-full hover:bg-gray-50 transition-colors border border-gray-300"
                >
                  <Play className="mr-2 h-4 w-4" />
                  Watch Demo
                </Link>
              </div>
              <div className="mt-10 flex items-center gap-4">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-400 to-purple-400 border-2 border-white shadow-sm" />
                  ))}
                </div>
                <p className="text-sm text-gray-600">
                  Trusted by <span className="font-semibold text-gray-900">2,000+</span> marketing teams
                </p>
              </div>
            </div>
            <div className="relative perspective-1000">
              <div className="relative rounded-3xl overflow-hidden bg-white p-12 shadow-xl border border-gray-200 will-change-transform hover:rotate-y-3 transition-transform duration-500">
                {heroImage}
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/5 to-transparent pointer-events-none" />
              </div>
              
              {/* Floating cards */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 w-48 border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-green-400 to-emerald-400 flex items-center justify-center shadow-sm">
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">ROAS</p>
                    <p className="text-lg font-semibold text-gray-900">+147%</p>
                  </div>
                </div>
              </div>
              
              <div className="absolute -top-6 -right-6 bg-white rounded-2xl shadow-xl p-4 w-40 border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-amber-400 to-orange-400 flex items-center justify-center shadow-sm">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Conversion</p>
                    <p className="text-lg font-semibold text-gray-900">+82%</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MarketingChannels - Hero ve GlowyAI arasına taşındı */}
      <div id="marketing-channels" className="bg-white py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <MarketingChannels />
        </div>
      </div>

      {/* GlowyAI Chat Demo Section */}
      <div className="py-16 bg-gradient-to-b from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Sol taraf - Chat Demo - 8 birim */}
            <div className="lg:col-span-8">
              <GlowyAIChat />
            </div>

            {/* Sağ taraf - Başlık ve Maskot - 4 birim */}
            <div className="lg:col-span-4 text-left lg:pl-6">
              <div className="relative">
                {/* Arkaplan dekoratif elementi */}
                <div className="absolute -top-10 -right-10 w-64 h-64 bg-gradient-to-br from-blue-500/10 to-purple-500/5 rounded-full blur-3xl -z-10"></div>
                
                {/* Badge */}
                <div className="inline-flex items-center px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full mb-2 text-xs font-medium tracking-wide">
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                  AI ASSISTANT
                </div>

                <h3 className="text-2xl font-bold text-gray-900 leading-tight">
                  Meet Your Marketing <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Co-Pilot</span>
                </h3>
                
                <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                  Manage and optimize your campaigns through natural conversations with our AI assistant.
                </p>

                {/* Özellik listesi - daha modern ve kompakt */}
                <div className="mt-4 space-y-2">
                  <div className="flex items-start gap-3 group p-2 rounded-xl transition-colors hover:bg-blue-50/50">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-md shadow-blue-500/20 flex-shrink-0 mt-0.5">
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 group-hover:text-blue-700 transition-colors text-sm">Intelligent campaign optimization</h4>
                      <p className="mt-0.5 text-xs text-gray-600">Automatically identify opportunities to improve performance</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 group p-2 rounded-xl transition-colors hover:bg-blue-50/50">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-md shadow-indigo-500/20 flex-shrink-0 mt-0.5">
                      <BarChart3 className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 group-hover:text-indigo-700 transition-colors text-sm">Real-time performance insights</h4>
                      <p className="mt-0.5 text-xs text-gray-600">Get instant alerts and actionable insights for campaigns</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 group p-2 rounded-xl transition-colors hover:bg-blue-50/50">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center shadow-md shadow-purple-500/20 flex-shrink-0 mt-0.5">
                      <Target className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 group-hover:text-purple-700 transition-colors text-sm">Automated budget recommendations</h4>
                      <p className="mt-0.5 text-xs text-gray-600">Optimize spend allocation based on performance data</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* AI Maskot */}
              <div className="relative mt-4">
                <div className="absolute -z-10 inset-0 bg-gradient-to-tr from-blue-100/40 to-purple-100/40 rounded-3xl blur-xl opacity-70"></div>
                <img 
                  src="/14rob.png" 
                  alt="GlowyAI Assistant" 
                  className="w-full max-w-[220px] h-auto object-contain mx-auto drop-shadow-xl"
                />

                {/* GlowyAI açıklaması - maskotun altına taşındı */}
                <p className="text-center text-xs text-gray-500 mt-4">
                  GlowyAI can analyze performance across all major platforms
                </p>
                <div className="flex justify-center space-x-4 mt-2">
                  <img src="/google_ads-512.webp" alt="Google Ads" className="h-5 opacity-70" />
                  <img src="/6033716.png" alt="Meta Ads" className="h-5 opacity-70" />
                  <img src="/Google-Analytics-Logo.png" alt="Google Analytics" className="h-5 opacity-70" />
                  <img src="/download.png" alt="TikTok" className="h-5 opacity-70" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Device Integration Section */}
      <div className="bg-white py-20">
        <DeviceIntegration />
      </div>

      {/* Data Flow Animation Section */}
      <div className="bg-gradient-to-b from-white to-blue-50 py-20">
        <DataFlowAnimation />
      </div>

      {/* Analytics Dashboard Section */}
      <div className="py-24 bg-white">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-4">
              <h2 className="text-3xl font-bold tracking-tight text-gray-900">
                Powerful Analytics Dashboard
                <span className="block text-blue-600 mt-2">Built for Performance</span>
              </h2>
              <p className="mt-4 text-base text-gray-600">
                Get a comprehensive view of your marketing performance across all channels. Our intuitive dashboard provides real-time insights and actionable recommendations.
              </p>
              
              {/* Dashboard Selector */}
              <div className="mt-8 space-y-4">
                {dashboards.map((dashboard) => (
                  <button 
                    key={dashboard.id}
                    onClick={() => setSelectedDashboard(dashboard.id)}
                    className={`w-full flex gap-4 p-5 rounded-xl cursor-pointer transition-all duration-300 group ${
                      selectedDashboard === dashboard.id
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 shadow-lg'
                        : 'bg-white border-2 border-gray-100 hover:border-blue-200 hover:bg-blue-50/50'
                    }`}
                  >
                    <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 ${
                      selectedDashboard === dashboard.id
                        ? 'bg-white/20 text-white'
                        : 'bg-blue-50 text-blue-500 group-hover:bg-blue-100'
                    }`}>
                      {dashboard.icon}
                    </div>
                    <div className="flex-1 text-left">
                      <h3 className={`text-lg font-semibold transition-colors duration-300 ${
                        selectedDashboard === dashboard.id ? 'text-white' : 'text-gray-900'
                      }`}>
                        {dashboard.title}
                      </h3>
                      <p className={`mt-1 text-sm ${
                        selectedDashboard === dashboard.id ? 'text-blue-100' : 'text-gray-600'
                      }`}>
                        {dashboard.description}
                      </p>
                    </div>
                    <div className={`flex items-center transition-transform duration-300 ${
                      selectedDashboard === dashboard.id ? 'translate-x-0 opacity-100' : '-translate-x-2 opacity-0'
                    }`}>
                      <ArrowRight className={`w-5 h-5 ${
                        selectedDashboard === dashboard.id ? 'text-white' : 'text-blue-500'
                      }`} />
                    </div>
                  </button>
                ))}
              </div>
            </div>
            
            <div className="lg:col-span-8 relative">
              <div className="relative rounded-3xl overflow-hidden bg-white p-6 shadow-2xl border border-gray-100">
                <img
                  src={dashboardImages[selectedDashboard]}
                  alt={`${dashboards.find(d => d.id === selectedDashboard)?.title} Dashboard`}
                  className="w-full h-auto rounded-xl transition-opacity duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/5 to-transparent pointer-events-none" />
              </div>
              
              {/* Floating Stats Cards */}
              <div className="absolute -top-6 -right-6 bg-white rounded-2xl shadow-lg p-4 border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">
                      {dashboards.find(d => d.id === selectedDashboard)?.stats.label}
                    </p>
                    <p className="text-xl font-bold text-gray-900">
                      {dashboards.find(d => d.id === selectedDashboard)?.stats.value}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Features Section */}
      <div id="detailed-features" ref={detailedFeaturesRef} className="bg-white">
        {isVisible.detailedFeatures && (
          <Suspense fallback={<SectionLoader />}>
            <FeatureSection />
          </Suspense>
        )}
      </div>

      {/* Logos Section - Animated Marquee (sayfanın altına taşındı) */}
      <div className="py-12 bg-white border-y border-gray-100">
        <p className="text-center text-sm text-gray-500 font-medium mb-8">TRUSTED BY INNOVATIVE COMPANIES</p>
        
        {/* Logo Slider */}
        <div className="relative overflow-hidden">
          {/* Sliding Container */}
          <div className="flex logo-animation">
            {/* İlk set logolar */}
            <div className="flex items-center justify-around shrink-0" style={{ minWidth: '100vw' }}>
              <img src="/6033716.png" alt="Meta" className="h-8 grayscale hover:grayscale-0 transition-all" />
              <img src="/CRTO-25f99ae9.png" alt="Criteo" className="h-8 grayscale hover:grayscale-0 transition-all" />
              <img src="/facebook.png" alt="Facebook" className="h-8 grayscale hover:grayscale-0 transition-all" />
              <img src="/Google-Analytics-Logo.png" alt="Google Analytics" className="h-8 grayscale hover:grayscale-0 transition-all" />
              <img src="/logoicon.png" alt="Glowytics" className="h-8 grayscale hover:grayscale-0 transition-all" />
            </div>
            {/* İkinci set logolar (aynı logolar) */}
            <div className="flex items-center justify-around shrink-0" style={{ minWidth: '100vw' }}>
              <img src="/6033716.png" alt="Meta" className="h-8 grayscale hover:grayscale-0 transition-all" />
              <img src="/CRTO-25f99ae9.png" alt="Criteo" className="h-8 grayscale hover:grayscale-0 transition-all" />
              <img src="/facebook.png" alt="Facebook" className="h-8 grayscale hover:grayscale-0 transition-all" />
              <img src="/Google-Analytics-Logo.png" alt="Google Analytics" className="h-8 grayscale hover:grayscale-0 transition-all" />
              <img src="/logoicon.png" alt="Glowytics" className="h-8 grayscale hover:grayscale-0 transition-all" />
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Suspense fallback={<SectionLoader />}>
        <Footer />
      </Suspense>

      {/* Add this CSS to your global styles or as a style tag */}
      <style jsx>{`
        .logo-animation {
          animation: slide 30s linear infinite;
        }

        @keyframes slide {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }

        .logo-animation:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
} 

